import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Search, ShieldCheck, Trash2 } from "lucide-react";
import { del, errorMessage } from "@/lib/api";
import { fetchOfficers, useAsync } from "@/lib/queries";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { OfficerDialog } from "./OfficerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Officers() {
  const { data, loading, error, reload } = useAsync(() => fetchOfficers(), []);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, officer: null });
  const [toDelete, setToDelete] = useState(null);

  const rows = useMemo(() => (data || []).filter((o) =>
    [o.full_name, o.company, o.phone].join(" ").toLowerCase().includes(q.toLowerCase().trim())), [data, q]);

  const remove = async () => {
    try {
      await del(`/officers/${toDelete.id}`);
      toast.success("Officer deleted");
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  return (
    <div className="space-y-6" data-testid="officers-page">
      <PageHeader eyebrow="Team" title="Warrant Officers" description="Each warrant run has one warrant officer."
        actions={<Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setDialog({ open: true, officer: null })} data-testid="add-officer-button"><Plus className="mr-1 h-4 w-4" /> Add officer</Button>} />
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search officers" value={q} onChange={(e) => setQ(e.target.value)} className="bg-white pl-9" aria-label="Search officers" data-testid="officers-search-input" />
      </div>
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !data && <LoadingRows />}
      {data && !rows.length && <EmptyState icon={ShieldCheck} title="No officers yet" description="Add officers so they can be selected on warrant runs." testId="officers-empty" />}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <Table data-testid="officers-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Name</TableHead><TableHead>Company</TableHead><TableHead>Telephone</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((o) => (
                <TableRow key={o.id} data-testid={`officer-row-${o.id}`}>
                  <TableCell className="font-semibold text-slate-900">{o.full_name}</TableCell>
                  <TableCell className="text-sm">{o.company || "—"}</TableCell>
                  <TableCell className="font-data text-sm"><a href={`tel:${o.phone}`} className="text-blue-700 hover:underline">{o.phone || "—"}</a></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" aria-label="Edit officer" onClick={() => setDialog({ open: true, officer: o })} data-testid={`edit-officer-${o.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" aria-label="Delete officer" className="text-rose-600 hover:bg-rose-50" onClick={() => setToDelete(o)} data-testid={`delete-officer-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <OfficerDialog open={dialog.open} officer={dialog.officer} onOpenChange={(o) => setDialog({ ...dialog, open: o })} onSaved={reload} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Delete officer?"
        description={`${toDelete?.full_name} will be permanently deleted. Officers assigned to warrant runs can't be deleted.`}
        confirmLabel="Delete officer" onConfirm={remove} />
    </div>
  );
}
